import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box,
  Chip
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import { closeAllDetachableComponents } from '../utils/detachableHelpers';

const InactivityWarning = () => {
  const { inactivityTimeout, resetInactivityTimer, logout, activityIntensity } = useAuth();
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(60); // 60 second warning
  const countdownRef = useRef(null);
  const warningTimerRef = useRef(null);

  // Calculate adjusted timeout text
  const getTimeoutAdjustment = () => {
    if (activityIntensity === 'high') {
      return { text: 'Extended', color: 'success' };
    } else if (activityIntensity === 'low') {
      return { text: 'Reduced', color: 'warning' };
    } else {
      return { text: 'Standard', color: 'info' };
    }
  };

  // Setup warning to appear 60 seconds before timeout. This used to only
  // (re)schedule when inactivityTimeout/activityIntensity changed - neither
  // of which changes on ordinary user activity (activityIntensity usually
  // stays 'normal' for the whole session) - so a continuously active user
  // still got a warning counted down from the *original* mount-time
  // deadline, and got force-logged-out at the end of that countdown despite
  // AuthContext's real timer having been pushed back out by their activity
  // the whole time. It also meant the warning would only ever schedule
  // once: after it fired (or if activityIntensity never changed again), no
  // future warning would ever be shown for the rest of the session.
  // AuthContext's resetInactivityTimer now dispatches an
  // 'inactivityTimerReset' window event every time the real logout timer is
  // reset - listen for that and reschedule this warning to match, dismissing
  // it if it's currently open (real activity means there's no longer an
  // imminent timeout to warn about).
  useEffect(() => {
    if (!inactivityTimeout) return;

    const scheduleWarning = () => {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }

      // Calculate when to show the warning (60 seconds before timeout)
      // Need to consider the adjusted timeout based on activity intensity
      let adjustedTimeout = inactivityTimeout;
      if (activityIntensity === 'high') {
        adjustedTimeout = Math.min(inactivityTimeout * 1.25, inactivityTimeout + (15 * 60 * 1000));
      } else if (activityIntensity === 'low') {
        adjustedTimeout = Math.max(inactivityTimeout * 0.75, 10 * 60 * 1000);
      }

      const warningTime = adjustedTimeout - (60 * 1000);

      // Ensure the warning appears at least 1 second after being scheduled,
      // for very short timeouts or edge cases
      const safeWarningTime = Math.max(warningTime, 1000);

      warningTimerRef.current = setTimeout(() => {
        setOpen(true);
        startCountdown();
      }, safeWarningTime);
    };

    const handleActivityReset = () => {
      setOpen(false);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      scheduleWarning();
    };

    scheduleWarning();
    window.addEventListener('inactivityTimerReset', handleActivityReset);

    return () => {
      window.removeEventListener('inactivityTimerReset', handleActivityReset);
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inactivityTimeout, activityIntensity]);
  
  const startCountdown = () => {
    setCountdown(60);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  const handleStayLoggedIn = () => {
    setOpen(false);
    clearInterval(countdownRef.current);
    resetInactivityTimer();
  };
  
  const handleLogout = () => {
    setOpen(false);
    clearInterval(countdownRef.current);
    // Close detachable components before logout
    closeAllDetachableComponents();
    logout();
    // Navigate to login after logout is now handled in the logout function
  };

  const timeoutInfo = getTimeoutAdjustment();
  
  return (
    <Dialog open={open} onClose={handleStayLoggedIn}>
      <DialogTitle>
        Session Timeout Warning
      </DialogTitle>
      <DialogContent>
        <Typography>
          Your session is about to expire due to inactivity. You will be automatically 
          logged out in {countdown} seconds.
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
          <SpeedIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Activity Level: 
          </Typography>
          <Chip 
            size="small" 
            label={activityIntensity.charAt(0).toUpperCase() + activityIntensity.slice(1)} 
            color={timeoutInfo.color}
            sx={{ ml: 1 }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Timeout: 
          </Typography>
          <Chip 
            size="small" 
            label={timeoutInfo.text} 
            color={timeoutInfo.color}
            sx={{ ml: 1 }}
          />
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={(countdown / 60) * 100} 
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogout} color="secondary">
          Logout Now
        </Button>
        <Button onClick={handleStayLoggedIn} color="primary" variant="contained">
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InactivityWarning; 